import React, { useState, useRef, ChangeEvent, DragEvent } from 'react';
import { ArrowUpload24Regular } from "@fluentui/react-icons";
import styles from './FileDropZone.module.css';

interface FileDropZoneProps {
    onFilesAdded: (files: File[]) => void;
    multiple?: boolean;
    accept?: string; // e.g., ".pdf,.docx"
    label?: string; // Allow customizing the text
}

export const FileDropZone = ({ 
    onFilesAdded, 
    multiple = true, 
    accept,
    label = "Drag & drop or Browse" 
}: FileDropZoneProps) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // handle drag entering the zone
    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    // handle drag leaving the zone
    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    // handle the actual drop
    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const fileList = Array.from(e.dataTransfer.files);
            onFilesAdded(fileList);
        }
    };

    // handle click to open file explorer
    const handleClick = () => {
        fileInputRef.current?.click();
    };

    // handle file selection via explorer
    const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const fileList = Array.from(e.target.files);
            onFilesAdded(fileList);
            
            // Reset value so the same file can be selected again if needed
            e.target.value = ''; 
        }
    };

    return (
        <div
            className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
        >
            <input
                type="file"
                multiple={multiple}
                accept={accept}
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
            />
            <div className={styles.dropZoneContent}>
                <ArrowUpload24Regular 
                    className={styles.largeDropIcon} 
                    // Keeping the static color default, but moving it to CSS is preferred if possible
                    style={{color: '#00a66d'}} 
                />
                <span 
                    className={styles.dropZoneTextSmall}
                    // This creates the "Browse" bolding effect if the default label is used
                    dangerouslySetInnerHTML={{ 
                        __html: label.replace("Browse", "<strong>Browse</strong>") 
                    }}
                />
            </div>
        </div>
    );
};
